/**
 * PreferencesCard Component
 *
 * Dismissible prompt card that appears on the Home tab encouraging
 * operators to answer 3 preference questions. Uses OperatorCard with
 * a gradient background override.
 *
 * "Personalize" opens the PreferencesSheet BottomSheet.
 * "Later" snoozes the card for the current session.
 *
 * bg-op-card (background-color) and bg-gradient-to-r (background-image)
 * use different CSS properties, so both apply without conflict -- the
 * gradient visually dominates.
 */

import { Sparkles } from 'lucide-react';
import { OperatorCard } from './OperatorCard';
import { cn } from '../../../lib/utils';

interface PreferencesCardProps {
  /** Opens the PreferencesSheet BottomSheet */
  onPersonalize: () => void;
  /** Snoozes the card for the current session */
  onLater: () => void;
}

export function PreferencesCard({ onPersonalize, onLater }: PreferencesCardProps) {
  return (
    <div style={{ animation: 'fadeInUp 0.4s ease-out 0.3s both' }}>
      <OperatorCard className="bg-gradient-to-r from-guild-green/5 to-guild-mint/10">
        <div className="flex gap-3">
          {/* Icon */}
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
            <Sparkles size={22} strokeWidth={1.5} />
          </div>

          {/* Text */}
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-op-text-primary">
              Personalize your experience
            </p>
            <p className="mt-0.5 text-sm text-op-text-secondary">
              Answer 3 quick questions to get better training recommendations.
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onPersonalize}
            className={cn(
              'min-h-[44px] flex-1 rounded-xl px-4 text-sm font-semibold',
              'bg-guild-green text-white',
              'active:bg-guild-green/90 transition-colors duration-150',
              'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
            )}
          >
            Personalize
          </button>
          <button
            type="button"
            onClick={onLater}
            className={cn(
              'min-h-[44px] rounded-xl px-4 text-sm font-medium',
              'text-op-text-secondary',
              'active:bg-gray-100 transition-colors duration-150',
              'focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
            )}
          >
            Later
          </button>
        </div>
      </OperatorCard>
    </div>
  );
}
